import React from "react";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import "./auth.css";
import Turnstile from "react-turnstile";
// Import the custom hook
import { useLoginLogic } from "./useLoginLogic";
// Importar la imagen de fondo
import backgroundImage from "../../assets/backgrounds/twitterCover.jpg";

const Login: React.FC = () => {
  // Use the custom hook that contains all the login logic
  const {
    formData,
    errors,
    isLoading,
    rememberMe,
    turnstileToken,
    turnstileKey,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
    handleTurnstileSuccess,
    handleTurnstileError,
    handleTurnstileExpire,
  } = useLoginLogic();

  // Toggle ojito de la contraseña. Por defecto OFF (oculta) — el campo arranca
  // como type="password" y solo se revela mientras el usuario lo mantiene
  // activado.
  const [showPassword, setShowPassword] = React.useState(false);

  // Clave de sitio de Cloudflare Turnstile.
  // En producción REACT_APP_TURNSTILE_SITE_KEY debe estar definida en el .env de build.
  // "1x00000000000000000000AA" es la test-key de Cloudflare (acepta cualquier token);
  // solo se usa como fallback fuera de producción.
  const TURNSTILE_TEST_KEY = "1x00000000000000000000AA";
  const siteKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY ??
    (process.env.NODE_ENV !== "production" ? TURNSTILE_TEST_KEY : "");
  if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_TURNSTILE_SITE_KEY) {
    console.error(
      "[Turnstile] REACT_APP_TURNSTILE_SITE_KEY no está definida. " +
        "El formulario de login no está protegido en producción."
    );
  }

  return (
    <div className="login-page">
      <div
        className="login-left-panel"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Se elimina el div welcome-content que contenía el texto */}
      </div>

      <div className="login-right-panel">
        <div className="login-container">
          <div className="login-header">
            <h2>Bienvenido a Aloja Fácil</h2>
            <p>Ingresa tus credenciales para continuar</p>
          </div>

          {errors.general && (
            <div className="error-message" role="alert" aria-live="assertive">
              <span className="error-icon">⚠️</span>
              {errors.general}
            </div>
          )}

          {/*
            autoComplete explícito ("username" / "current-password") + readOnly
            durante el submit en lugar de disabled: evita que Chrome re-dispare
            autofill al rehabilitar el input tras un login fallido y pise con
            credenciales guardadas el email que el usuario acababa de tipear.
          */}
          <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
            <div className="form-group">
              <label htmlFor="email">Correo electrónico</label>
              <div className="input-container">
                <span className="input-icon">✉️</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="nombre@ejemplo.com"
                  className={errors.email ? "input-error" : ""}
                  autoComplete="username"
                  // El teclado del celular no debe capitalizar ni «corregir» un correo
                  // (round 2026-10-28).
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  readOnly={isLoading}
                />
              </div>
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <div className="input-container">
                <span className="input-icon">🔑</span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Ingresa tu contraseña"
                  className={`has-toggle ${errors.password ? "input-error" : ""}`}
                  autoComplete="current-password"
                  // Mismo motivo: al mostrar la contraseña (type="text") Gboard la trata como
                  // texto y puede capitalizar la primera letra o agregar un espacio al final.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  readOnly={isLoading}
                />
                {/* Ojito mostrar/ocultar. type="button" para no enviar el form.
                    tabIndex -1 para no interrumpir el tabbing email→password→submit. */}
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                  }
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
              {errors.password && (
                <span className="error-text">{errors.password}</span>
              )}
            </div>

            <div className="form-options">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={handleCheckboxChange}
                  disabled={isLoading}
                />
                <label htmlFor="remember">Recordarme</label>
              </div>
              <div className="forgot-password">
                <a href="/recuperar-contrasena">¿Olvidaste tu contraseña?</a>
              </div>
            </div>

            <div className="turnstile-container">
              {/*
                `key={turnstileKey}` fuerza un re-mount limpio del widget
                cada vez que necesitamos un token nuevo (tras submit
                fallido o token expirado). Sin esto usábamos
                `bound.reset()` del lib v1.1.4, que tiene un bug donde el
                iframe interno queda desincronizado ("Form submission
                canceled because the form is not connected") y los tokens
                nuevos quedan huérfanos en CF → `invalid-input-response`
                por 3-4 intentos hasta que el widget se "asienta".
                Round 2026-04-26 fase 8.
              */}
              <Turnstile
                key={turnstileKey}
                sitekey={siteKey as string}
                onSuccess={handleTurnstileSuccess}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="light"
                language="es"
                // refreshExpired="auto": si el token expira (5 min idle)
                // el widget regenera uno nuevo automáticamente sin que el
                // usuario tenga que recargar la página.
                refreshExpired="auto"
                retry="auto"
              />
              {errors.turnstile && (
                <span className="error-text">{errors.turnstile}</span>
              )}
            </div>

            {/*
              Botón deshabilitado hasta que el widget Turnstile entregue un
              token. Sin este guard el usuario podía clickear "Iniciar
              sesión" justo entre un reset y la regeneración → enviábamos
              token vacío o stale → CF respondía `invalid-input-response`.
              Round 2026-04-26 fase 8.
            */}
            <button
              type="submit"
              className="login-button"
              disabled={isLoading || !turnstileToken}
            >
              {isLoading ? (
                <span className="loading-container">
                  <div className="spinner-icon"></div>
                  <span className="loading-text">Cargando...</span>
                </span>
              ) : !turnstileToken ? (
                "Verificando seguridad..."
              ) : (
                "Iniciar sesión"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
